import { APP_CONFIG } from "../constants";
import { AppError } from "../utils/errorHandler";

export type MobileReleaseMeta = {
  id: number;
  versionCode: number;
  versionName: string;
  sha256: string;
  fileSizeBytes: number;
  createdAt: string;
  fileOnDisk?: boolean;
};

export type MobileAppUserVersionRow = {
  usuarioId: number;
  nome: string;
  email: string;
  lojaNome: string | null;
  mobileAppVersionCode: number | null;
  mobileAppVersionName: string | null;
  mobileAppVersionReportedAt: string | null;
};

export type MobileAppUserVersionsPage = {
  usuarios: MobileAppUserVersionRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

async function parseError(response: Response): Promise<never> {
  try {
    const data = (await response.json()) as {
      message?: string | string[];
    };
    const msg = Array.isArray(data.message)
      ? data.message[0]
      : data.message || response.statusText;
    throw new AppError(msg, response.status);
  } catch (e) {
    if (e instanceof AppError) throw e;
    throw new AppError(response.statusText, response.status);
  }
}

export const mobileAppAdminService = {
  getCurrentRelease: async (): Promise<MobileReleaseMeta | null> => {
    const res = await fetch(
      `${APP_CONFIG.API_URL}/mobile-app/releases/current`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
          Accept: "application/json",
        },
      }
    );
    if (!res.ok) return parseError(res);
    const data = (await res.json()) as { release: MobileReleaseMeta | null };
    return data.release ?? null;
  },

  getUserVersions: async (params?: {
    page?: number;
    limit?: number;
    q?: string;
  }): Promise<MobileAppUserVersionsPage> => {
    const search = new URLSearchParams();
    const page = params?.page ?? 1;
    const limit = params?.limit ?? 10;
    search.set("page", String(page));
    search.set("limit", String(limit));
    if (params?.q?.trim()) {
      search.set("q", params.q.trim());
    }
    const res = await fetch(
      `${APP_CONFIG.API_URL}/admin/mobile-app/user-versions?${search.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
          Accept: "application/json",
        },
      }
    );
    if (!res.ok) return parseError(res);
    return (await res.json()) as MobileAppUserVersionsPage;
  },

  publishApk: async (
    input: { file: File },
  ): Promise<{ release: MobileReleaseMeta; versionWarning: string | null }> => {
    const fd = new FormData();
    fd.append("apk", input.file);
    const res = await fetch(`${APP_CONFIG.API_URL}/admin/mobile-app/releases`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
      },
      body: fd,
    });
    if (!res.ok) return parseError(res);
    const data = (await res.json()) as {
      release: MobileReleaseMeta;
      versionWarning?: string | null;
    };
    return {
      release: data.release,
      versionWarning: data.versionWarning ?? null,
    };
  },

  restoreCurrentApk: async (input: { file: File }): Promise<MobileReleaseMeta> => {
    const fd = new FormData();
    fd.append("apk", input.file);
    const res = await fetch(
      `${APP_CONFIG.API_URL}/admin/mobile-app/releases/restore-current`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
        body: fd,
      },
    );
    if (!res.ok) return parseError(res);
    const data = (await res.json()) as { release: MobileReleaseMeta };
    return data.release;
  },

  /** Baixa o .apk da versão ativa (requer login). */
  downloadCurrentApk: async (): Promise<void> => {
    const res = await fetch(
      `${APP_CONFIG.API_URL}/mobile-app/releases/current/download`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
      }
    );
    if (!res.ok) return parseError(res);

    const blob = await res.blob();
    const cd = res.headers.get("Content-Disposition") ?? "";
    const match = /filename="?([^";]+)"?/i.exec(cd);
    const filename = match?.[1]?.trim() || "DomBelloni.apk";

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
